import {
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '@github-ui/shared-dashboard-lists/shared/consts/sorting-consts'
import type {EmptyListState} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'

import {isExplicitIssueQuery} from '../helpers/is-explicit-issue-query'

interface IssuesListControlsOptions {
  /** Initial density; server-seeded from the layout payload. */
  readonly initialCompactMode: boolean
  /** FormData key for the legacy density-preference endpoint. */
  readonly compactModePreferenceKey: string
  readonly isFetching: boolean
  readonly currentPage: number
  readonly fetchedQuery: string
  readonly defaultSortingKey: string
  readonly totalCount: number
  readonly totalPages: number
  readonly errorMessage?: string
}

/**
 * Shared density + screen-reader wiring for the issues list surfaces (dashboard and
 * repo index). Persists the density preference through the shared pulls/dashboard
 * cookie and emits the result-count / empty-state announcements, so both surfaces
 * behave identically without duplicating the glue.
 *
 * Density is passed through as a seed only — `ListView` owns the live toggle state,
 * so no local state is needed here; `onCompactModeChange` just persists the choice.
 */
export function useIssuesListControls({
  initialCompactMode,
  compactModePreferenceKey,
  isFetching,
  currentPage,
  fetchedQuery,
  defaultSortingKey,
  totalCount,
  totalPages,
  errorMessage,
}: IssuesListControlsOptions): {
  compactMode: boolean
  onCompactModeChange: (value: boolean) => void
  emptyListState: EmptyListState
} {
  const {updateCompactModeSetting} = useUpdateCompactModeSetting(initialCompactMode, compactModePreferenceKey, {
    persistAsPullsPreference: true,
  })

  // The issues surfaces list mixed issue/PR results, so announce a neutral "result"
  // noun unless the query is explicitly issue-only.
  const isMixedView = !isExplicitIssueQuery(fetchedQuery)
  const {emptyListState} = useScreenReaderAnnouncements(
    'issues',
    isFetching,
    currentPage,
    fetchedQuery,
    defaultSortingKey,
    totalCount,
    totalPages,
    errorMessage,
    isMixedView ? {singular: 'result', plural: 'results'} : undefined,
    {...defaultSortingOptions, ...issuesDashboardSortingOptions},
  )

  return {compactMode: initialCompactMode, onCompactModeChange: updateCompactModeSetting, emptyListState}
}
